import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Input,
  Button,
  Select,
  Space,
  Empty,
  Spin,
  Typography,
  Popconfirm,
  message,
} from "antd";
import { formatIst, getUserDisplayName } from "../Utils/preLeadPayload";
import {
  getPreLeadNotes,
  createPreLeadNote,
  updatePreLeadNote,
  deletePreLeadNote,
} from "../Actions/preLeadsAction";

const { TextArea } = Input;
const { Text } = Typography;

// contact_via is free text server-side - there is no enum - so the values are
// fixed here to keep them consistent across the app.
const CONTACT_VIA_OPTIONS = [
  { value: "phone", label: "Phone" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "email", label: "Email" },
  { value: "in_person", label: "In person" },
];

/**
 * Combined activity timeline: employee-written notes interleaved with the
 * system audit entries the backend writes on assign/transfer/dismiss/reopen.
 * System entries are rendered muted so they read as history, not comments.
 */
const PreLeadNotes = ({ preLeadId, currentUser, canWrite, onChanged }) => {
  const dispatch = useDispatch();
  const { notes, isGetNotesLoading, isSaveNoteLoading } = useSelector(
    (state) => state.preLeads,
  );

  const [text, setText] = useState("");
  const [contactVia, setContactVia] = useState("phone");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  useEffect(() => {
    if (preLeadId) dispatch(getPreLeadNotes(preLeadId));
  }, [dispatch, preLeadId]);

  // notes_count lives on the pre-lead, so the parent is told to refetch too -
  // otherwise the Activity badge keeps showing the pre-write count.
  const refresh = () => {
    dispatch(getPreLeadNotes(preLeadId));
    onChanged && onChanged();
  };

  const handleAdd = () => {
    if (!text.trim()) {
      message.error("Note text is required");
      return;
    }
    dispatch(
      createPreLeadNote(
        preLeadId,
        { text: text.trim(), contact_via: contactVia },
        () => {
          setText("");
          message.success("Note added");
          refresh();
        },
        (error) => message.error(error || "Could not save the note"),
      ),
    );
  };

  const handleUpdate = (noteId) => {
    if (!editingText.trim()) {
      message.error("Note text is required");
      return;
    }
    dispatch(
      updatePreLeadNote(
        preLeadId,
        noteId,
        { text: editingText.trim() },
        () => {
          setEditingId(null);
          message.success("Note updated");
          refresh();
        },
        (error) => message.error(error || "Could not update the note"),
      ),
    );
  };

  const handleDelete = (noteId) => {
    dispatch(
      deletePreLeadNote(
        preLeadId,
        noteId,
        () => {
          message.success("Note deleted");
          refresh();
        },
        (error) => message.error(error || "Could not delete the note"),
      ),
    );
  };

  // Only manual notes are editable, and only by their author. Admins may also
  // delete anyone's manual note, but not edit it.
  const canEdit = (note) =>
    note.note_type !== "system" && note.user?.id === currentUser?.id;
  const canDelete = (note) =>
    note.note_type !== "system" &&
    (note.user?.id === currentUser?.id || currentUser?.role === "Admin");

  return (
    <Spin spinning={isGetNotesLoading || isSaveNoteLoading}>
      {canWrite && (
        <div style={{ marginBottom: 16 }}>
          <TextArea
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="What happened on this call?"
            maxLength={2000}
          />
          <Space style={{ marginTop: 8 }}>
            <Select
              value={contactVia}
              onChange={setContactVia}
              options={CONTACT_VIA_OPTIONS}
              style={{ width: 140 }}
            />
            <Button type="primary" onClick={handleAdd}>
              Add note
            </Button>
          </Space>
        </div>
      )}

      {notes.length === 0 && !isGetNotesLoading && (
        <Empty description="No activity yet" />
      )}

      {notes.map((note) => {
        const isSystem = note.note_type === "system";
        return (
          <div
            key={note.id}
            style={{
              padding: "8px 12px",
              marginBottom: 8,
              borderLeft: `3px solid ${isSystem ? "#d9d9d9" : "#1c3988"}`,
              background: isSystem ? "transparent" : "#fafafa",
              borderRadius: 2,
            }}
          >
            {editingId === note.id ? (
              <>
                <TextArea
                  rows={2}
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                />
                <Space style={{ marginTop: 8 }}>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => handleUpdate(note.id)}
                  >
                    Save
                  </Button>
                  <Button size="small" onClick={() => setEditingId(null)}>
                    Cancel
                  </Button>
                </Space>
              </>
            ) : (
              <>
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: isSystem ? 12 : 14,
                    color: isSystem ? "#8c8c8c" : undefined,
                  }}
                >
                  {note.text}
                </div>
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {isSystem ? "System" : getUserDisplayName(note.user)}
                    {" · "}
                    {formatIst(note.created_at)}
                    {!isSystem && note.contact_via ? ` · ${note.contact_via}` : ""}
                  </Text>
                  {(canEdit(note) || canDelete(note)) && (
                    <Space size={0} style={{ marginLeft: 8 }}>
                      {canEdit(note) && (
                        <Button
                          type="link"
                          size="small"
                          onClick={() => {
                            setEditingId(note.id);
                            setEditingText(note.text);
                          }}
                        >
                          Edit
                        </Button>
                      )}
                      {canDelete(note) && (
                        <Popconfirm
                          title="Delete this note?"
                          onConfirm={() => handleDelete(note.id)}
                        >
                          <Button type="link" size="small" danger>
                            Delete
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}
    </Spin>
  );
};

export default PreLeadNotes;
